/**
 * Um PDF mínimo com uma imagem JPEG por página, sem biblioteca: o JPEG entra no PDF como
 * está (filtro `DCTDecode`), então basta escrever os objetos e a tabela de referências.
 * Página A4 em retrato (ou deitada, `A4_LANDSCAPE`); a imagem ocupa a página inteira.
 */

export interface PdfPage {
  /** Bytes do JPEG. */
  jpeg: Uint8Array;
  /** Dimensões do JPEG em pixels. */
  width: number;
  height: number;
}

export const A4 = { width: 595.28, height: 841.89 } as const;
export const A4_LANDSCAPE = { width: A4.height, height: A4.width } as const;

const encoder = new TextEncoder();

/** Texto do PDF em UTF-16BE, para o título com acento. */
function pdfText(text: string): string {
  let hex = 'FEFF';

  for (let i = 0; i < text.length; i++) hex += text.charCodeAt(i).toString(16).padStart(4, '0').toUpperCase();

  return `<${hex}>`;
}

export function buildPdf(
  pages: readonly PdfPage[],
  title: string,
  size: { width: number; height: number } = A4
): Uint8Array<ArrayBuffer> {
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;

  const push = (part: string | Uint8Array) => {
    const bytes = typeof part === 'string' ? encoder.encode(part) : part;

    chunks.push(bytes);
    length += bytes.length;
  };
  const object = (id: number, body: string | (string | Uint8Array)[]) => {
    offsets[id] = length;
    push(`${id} 0 obj\n`);

    for (const part of Array.isArray(body) ? body : [body]) push(part);

    push('\nendobj\n');
  };

  // 1 catálogo, 2 páginas, 3 informações; cada página usa 3 objetos (página, conteúdo, imagem).
  const pageId = (i: number) => 4 + i * 3;
  const w = size.width.toFixed(2);
  const h = size.height.toFixed(2);

  push('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');
  object(1, '<< /Type /Catalog /Pages 2 0 R >>');
  object(
    2,
    `<< /Type /Pages /Count ${pages.length} /Kids [${pages.map((_, i) => `${pageId(i)} 0 R`).join(' ')}] >>`
  );
  object(3, `<< /Title ${pdfText(title)} /Producer (Live Gospel) >>`);

  pages.forEach((page, i) => {
    const id = pageId(i);
    const content = `q ${w} 0 0 ${h} 0 0 cm /Im0 Do Q`;

    object(
      id,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] /Resources << /XObject << /Im0 ${id + 2} 0 R >> >> /Contents ${id + 1} 0 R >>`
    );
    object(id + 1, `<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
    object(id + 2, [
      `<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.jpeg.length} >>\nstream\n`,
      page.jpeg,
      '\nendstream',
    ]);
  });

  const count = pageId(pages.length);
  const xref = length;

  push(`xref\n0 ${count}\n0000000000 65535 f \n`);

  for (let id = 1; id < count; id++) push(`${String(offsets[id] ?? 0).padStart(10, '0')} 00000 n \n`);

  push(`trailer\n<< /Size ${count} /Root 1 0 R /Info 3 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  const out = new Uint8Array(length);
  let at = 0;

  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }

  return out;
}
